import { z } from 'zod'

export const quoteTaxCalculationBases = ['SUBTOTAL', 'RUNNING_TOTAL'] as const
export type QuoteTaxCalculationBase = (typeof quoteTaxCalculationBases)[number]

export const quoteTaxRoundingStrategies = ['PER_TAX_TOTAL_HALF_UP', 'PER_ITEM_HALF_UP'] as const
export type QuoteTaxRoundingStrategy = (typeof quoteTaxRoundingStrategies)[number]

export interface QuoteTaxRate {
  id: string
  code: string
  label: string
  ratePpm: number
  calculationBase: QuoteTaxCalculationBase
  displayOrder: number
}

export interface QuoteTaxProfile {
  id: string
  version: number
  label: string
  status: 'ACTIVE' | 'RETIRED'
  roundingStrategy?: QuoteTaxRoundingStrategy
  activatedAt: string
  rates: QuoteTaxRate[]
}

export interface QuoteTaxProfilesResult {
  profiles: QuoteTaxProfile[]
  meta: {
    source: 'supabase' | 'fixtures' | 'fixture-fallback'
    retrievedAt: string
    fallbackReason?: string
  }
}

export interface ActivateQuoteTaxProfileCommand {
  idempotencyKey: string
  label: string
  roundingStrategy: QuoteTaxRoundingStrategy
  rates: {
    code: string
    label: string
    ratePpm: number
    calculationBase: QuoteTaxCalculationBase
  }[]
}

export interface ActivateQuoteTaxProfileResult {
  profileId: string
  version: number
  status: 'ACTIVE' | 'RETIRED'
  activatedAt: string
}

export const quoteTaxProfileRowSchema = z.object({
  id: z.uuid(),
  version: z.number().int().positive(),
  label: z.string().min(1),
  status: z.enum(['ACTIVE', 'RETIRED']),
  rounding_strategy: z.enum(quoteTaxRoundingStrategies).nullable(),
  activated_at: z.string(),
})

export const quoteTaxRateRowSchema = z.object({
  id: z.uuid(),
  profile_id: z.uuid(),
  code: z.string().min(1),
  label: z.string().min(1),
  rate_ppm: z.number().int().min(0).max(1_000_000),
  calculation_base: z.enum(quoteTaxCalculationBases),
  display_order: z.number().int().min(1).max(10),
})

export function formatTaxRate(ratePpm: number): string {
  return new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 4 }).format(ratePpm / 10_000)
}

export function parsePercentToPpm(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!/^\d+(?:\.\d{1,4})?$/.test(normalized)) return null
  const percent = Number(normalized)
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null
  return Math.round(percent * 10_000)
}
